// importBackup.ts — orchestration for the backup-import flow.
//
// The wizard component (`ImportBackupWizard.tsx`) drives the UI; the
// pure logic of "given a decrypted backup config, produce the
// AppConfig that should be persisted on this fresh device" lives
// here so it's testable without rendering React.
//
// Two output shapes:
//   - "fast path" (master == backup password): the imported config
//     is ready to use as-is — no `pending_rotation` markers.
//   - "rotate path" (master != backup password): every synced
//     non-OPFS vault in the imported config gets a
//     `pending_rotation` marker so the existing
//     `retryPendingRotations` orchestrator can drive the
//     password-rotation loop after the OPFS vault is created.
//
// The OPFS entry from the backup is REPLACED on this device. The
// backup's OPFS entry referenced the source device's
// keybag/manifest; on this device a fresh OPFS vault is created
// with its own salt and id. Other entries (synced S3, unsynced)
// are carried over verbatim.

import type { AppConfig, PendingRotation, VaultEntry } from "../worker/api";

/**
 * Mint a stable opaque vault id for the new OPFS vault. Mirrors
 * `MasterSetupWizard`'s id format so the URL path style is
 * consistent across first-time-setup and import.
 */
export function newOpfsVaultId(): string {
  return `v_${crypto.randomUUID()}`;
}

/**
 * Inputs to {@link buildImportedAppConfig}. Splitting into a typed
 * struct avoids a 5-positional-argument signature that's easy to
 * misorder.
 */
export interface BuildImportedConfigInput {
  /** The decrypted `AppConfig` from the backup file. */
  readonly backup: AppConfig;
  /** The new OPFS vault id minted on this device. */
  readonly newOpfsId: string;
  /** OPFS adapter `subdir` for the new vault — empty string for the
   * origin root, matching `MasterSetupWizard`. */
  readonly opfsSubdir: string;
  /**
   * `true` iff the user picked a master different from the backup
   * file's password. When `true`, every synced non-OPFS vault in
   * the imported config gets a `pending_rotation` marker so the
   * existing rotation orchestrator can drive the per-vault
   * `changePassphrase(backupPassword → newMaster)` loop.
   */
  readonly rotateSynced: boolean;
  /** Unix-seconds timestamp; injected for test determinism. */
  readonly nowSeconds: number;
}

/**
 * Build the AppConfig to persist after importing a backup on a
 * fresh device.
 *
 * Algorithm:
 * 1. Mint a fresh OPFS entry (new id, OPFS adapter, synced state)
 *    and use it as the new primary.
 * 2. Carry every non-OPFS vault from the backup verbatim — same
 *    id, same display name, same adapter config including
 *    credentials.
 * 3. If `rotateSynced` is `true`, stamp a `pending_rotation` marker
 *    on every synced non-OPFS vault. `old_password_sealed_hex` is
 *    empty (sentinel for "re-prompt required"), matching the
 *    convention established in
 *    `masterRotation.ts::commitRotationResults`.
 * 4. Carry the backup's `preferences` block verbatim — theme,
 *    auto-lock, cache prefs.
 *
 * The function is pure: same inputs → same outputs.
 */
export function buildImportedAppConfig(input: BuildImportedConfigInput): AppConfig {
  const { backup, newOpfsId, opfsSubdir, rotateSynced, nowSeconds } = input;

  const newOpfs: VaultEntry = {
    id: newOpfsId,
    display_name: "primary",
    adapter: { type: "opfs", subdir: opfsSubdir },
    sync_state: "synced",
    pending_rotation: null,
    last_opened_at: nowSeconds,
  };

  const pendingMarker: PendingRotation = {
    old_password_sealed_hex: "",
    nonce_hex: "",
    started_at: nowSeconds,
  };

  const carriedOver: VaultEntry[] = backup.vaults
    .filter((v) => v.adapter.type !== "opfs")
    .map((v) => ({
      ...v,
      pending_rotation:
        rotateSynced && v.sync_state === "synced"
          ? pendingMarker
          : v.pending_rotation,
    }));

  return {
    format_version: backup.format_version,
    primary_vault_id: newOpfsId,
    vaults: [newOpfs, ...carriedOver],
    preferences: backup.preferences,
  };
}
